import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as OrgNameIcon } from '../../assets/icons/team-name-icon.svg';
import { ReactComponent as RightArrowIcon } from '../../assets/icons/arrow-right-icon.svg';
import './OrgCreation.css';
import LoadingScreen from '../../components/LoadingScreen/LoadingScreen';
import { UserContext, OrgContext, WorkspaceContext } from '../../objects/Context';
import axios from 'axios';
import { getPostAuthDestination } from '../../utils/oauthFlow';

const OrgSelection = () => {
    const { user, setUser } = useContext(UserContext);
    const { setOrg } = useContext(OrgContext);
    const { setWorkspace } = useContext(WorkspaceContext);
    const [orgs, setOrgs] = useState([]);
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        if (user?.id) {
            loadData();
        }
    }, [user?.id]);

    const loadData = async () => {
        await Promise.all([fetchUserOrgs(), fetchInvites()]);
        setLoading(false);
    };

    const fetchUserOrgs = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/users/get-user-orgs/${user.id}`,
                { withCredentials: true }
            );
            setOrgs(response.data.orgs || []);
        } catch (err) {
            console.error('Error fetching user orgs:', err);
        }
    };

    const fetchInvites = async () => {
        if (!user?.email) return;
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/users/get-org-invites/${user.email}`,
                { withCredentials: true }
            );
            const incoming = response.data?.invites || [];
            // Don't show invites for orgs the user already belongs to.
            const existingOrgIds = user.orgIds || [];
            setInvites(incoming.filter(invite => !existingOrgIds.includes(invite.orgId)));
        } catch (err) {
            setInvites([]);
        }
    };

    const handleSelectOrg = async (selectedOrg) => {
        setLoading(true);
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_API_BASE_URL}/api/users/select-org/${user.id}/${selectedOrg.id}`,
                {},
                { withCredentials: true }
            );
            // The backend resolves the user's role + access level for the selected
            // org (a user can be an admin in one org and a member in another).
            const updatedUser = response.data?.user;
            setUser((prev) => ({
                ...prev,
                orgId: selectedOrg.id,
                ...(updatedUser
                    ? { orgRole: updatedUser.orgRole, accessLevel: updatedUser.accessLevel }
                    : {}),
            }));
            setOrg(selectedOrg);
            setWorkspace(null);
            navigate(getPostAuthDestination());
        } catch (err) {
            console.error('Error selecting org:', err);
            setLoading(false);
        }
    };

    const handleAcceptInvite = async (invite) => {
        if (!invite?.orgId || !user?.id || !user?.email) return;
        setLoading(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/orgs/invite-accepted`,
                { orgId: invite.orgId, userId: user.id, email: user.email },
                { withCredentials: true }
            );
            const acceptedOrg = response.data;
            setUser((prev) => ({
                ...prev,
                orgId: acceptedOrg.id,
                orgIds: [...(prev.orgIds || []), acceptedOrg.id],
                orgRole: acceptedOrg.orgRole,
                accessLevel: acceptedOrg.accessLevel,
            }));
            setOrg(acceptedOrg);
            setWorkspace(null);
            navigate(getPostAuthDestination());
        } catch (err) {
            console.error('Error accepting invite:', err);
            setLoading(false);
        }
    };

    const signOut = async () => {
        try {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/logout`, {}, { withCredentials: true });
            setUser(null);
            setOrg(null);
            setWorkspace(null);
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    return (
        <div className="org-creation">
            {loading ? <LoadingScreen isLoading={loading} /> : (
                <div className="content">
                    <h2>Your Organizations</h2>
                    <p className="info-text">Choose an organization to continue{invites.length > 0 ? ', or accept a pending invitation.' : '.'}</p>
                    <div className="dots">
                        <div className="dot" />
                        <div className="dot" />
                        <div className="dot" />
                    </div>

                    {orgs.map((org) => (
                        <li key={org.id} className="input-wrapper invite" onClick={() => handleSelectOrg(org)}>
                            <OrgNameIcon className="icon" />
                            <div className="input">{org.name}</div>
                            <RightArrowIcon className="icon" />
                        </li>
                    ))}

                    {invites.length > 0 && (
                        <>
                            <p className="divider"> - pending invitations - </p>
                            {invites.map((invite) => (
                                <li key={invite.orgId} className="input-wrapper invite pending" onClick={() => handleAcceptInvite(invite)}>
                                    <OrgNameIcon className="icon" />
                                    <div className="input">
                                        {invite.orgName}
                                        <span className="invite-accept-hint">Accept invitation</span>
                                    </div>
                                    <RightArrowIcon className="icon" />
                                </li>
                            ))}
                        </>
                    )}

                    <p className="sign-out" onClick={signOut}>Sign out</p>
                </div>
            )}
        </div>
    );
};

export default OrgSelection;
